/**
 * Employment history, read only.
 *
 * Route: /my-workspace/employee-history/:id, opened in a NEW TAB from the
 * Show History button on the all users table.
 *
 * Shows two things, because they answer different questions:
 *
 *   SPELLS      each time the person has worked here. A rejoin is a second spell,
 *               and the gap between one exit date and the next joining date is the
 *               time they were away.
 *
 *   TIMELINE    every change, one line per field that moved. A single save can
 *               produce several lines: moving department, wing, designation and
 *               manager together writes four.
 *
 * This screen writes nothing and nothing else depends on it.
 * See the node portal repo, docs/user-role-tag-with-history/README.md
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';

import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import Loader3D from '../../../../components/Loader/Loader3D';
import apiRequest from '../../../../api/request';
import {
  helpForChangeType,
  meaningOfDate,
  HELP_TOOLTIP_PROPS,
} from '../../../../utils/constants/employmentChangeTypes';

/** dd/mm/yyyy for reading. The API speaks yyyy-mm-dd. */
const showDate = (value) => {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-');
  return `${d}/${m}/${y}`;
};

/** dd/mm/yyyy hh:mm, for showing when somebody actually typed a change in. */
const showStamp = (value) => {
  if (!value) return '';
  const text = String(value).replace('T', ' ');
  const [datePart, timePart = ''] = text.split(' ');
  const [y, m, d] = datePart.split('-');
  return `${d}/${m}/${y} ${timePart.slice(0, 5)}`.trim();
};

/**
 * Colour per change type, using the fixed ids from employment_change_type.
 * Ids are stable across environments, names are not, so match on the id.
 */
const TYPE_COLOUR = {
  1: 'success',  // Joining
  2: 'success',  // Rejoin
  3: 'error',    // Exit
  4: 'warning',  // Department Change
  5: 'warning',  // Wing Change
  6: 'info',     // Designation Change
  7: 'primary',  // Role Change
  8: 'default',  // Manager Change
  9: 'default',  // Office Change
  10: 'default', // Employment Type Change
  11: 'default', // Employment Status Change
  12: 'info',    // Probation Completed
  13: 'default', // Correction
};

export default function EmployeeHistory() {
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [person, setPerson] = useState(null);

  // `silent` fetches fresh data without putting the page back into its loading
  // state. Used right after starting a history, where a loader is already up.
  const load = async (cancelledRef, { silent = false } = {}) => {
      if (!silent) setLoading(true);
      // the history itself, plus the person's name for the heading
      const [historyRes, personRes] = await Promise.all([
        apiRequest({ method: 'get', url: `users/employee-tagging/${params.id}/history` }),
        apiRequest({ method: 'get', url: `users/employee-tagging/${params.id}` }),
      ]);
      if (cancelledRef && cancelledRef.done) return;

      if (historyRes?.status === 'success') {
        setData(historyRes.data);
        setPerson(personRes?.status === 'success' ? personRes.data.user : null);
        setError('');
      } else {
        setError(historyRes?.message || 'Could not load the history');
      }
      if (!silent) setLoading(false);
  };

  useEffect(() => {
    const ref = { done: false };
    load(ref);
    return () => { ref.done = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  if (error) {
    return (
      <GlobalWrapper title="Employment History" skelton={false}>
        <Alert severity="error">{error}</Alert>
      </GlobalWrapper>
    );
  }
  // GlobalWrapper's own skeleton stops after a second and leaves an empty page
  // behind, so this loader is ours and stays until there is something to show.
  if (loading || !data) {
    return (
      <GlobalWrapper title="Employment History" skelton={false}>
        <Loader3D />
      </GlobalWrapper>
    );
  }

  const { spells, timeline, empty } = data;

  return (
    <GlobalWrapper title="Employment History" skelton={false}>
      <Stack spacing={2}>
        {person && (
          <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography variant="h6">{person.name}</Typography>
            {person.empId && (
              <Typography variant="body2" color="text.secondary">
                {String(person.empId).toUpperCase()}
              </Typography>
            )}
          </Stack>
        )}

        {/* Nothing recorded is a normal state, not a fault. There is no button to
            create history by hand any more. History has ONE door, the tagging
            screen, so there is never a second way for it to begin and never a
            question about which one was used. */}
        {empty && (
          <Alert severity="info" icon={<Icon icon="mdi:information-outline" />}>
            <Typography variant="subtitle2">No employment history yet</Typography>
            <Typography variant="body2">
              Nothing has been recorded for this person. History begins the first
              time their tagging is saved, and every change after that is recorded
              on the day it happens.
            </Typography>
          </Alert>
        )}

        {/* ---------------- the spells ---------------- */}
        {spells.length > 0 && (
          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle2">Employment records</Typography>
              <Typography variant="caption" color="text.secondary">
                One row each time this person has worked here
              </Typography>
            </Box>
            <Divider />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                    <TableCell>Employee ID</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Exit reason</TableCell>
                    <TableCell>Probation completed</TableCell>
                    <TableCell>Recorded by</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {spells.map((s) => (
                    <TableRow key={s.employment_id}>
                      <TableCell>{showDate(s.joining_date)}</TableCell>
                      <TableCell>
                        {s.exit_date ? (
                          showDate(s.exit_date)
                        ) : (
                          <Chip size="small" color="success" variant="outlined" label="Current" />
                        )}
                      </TableCell>
                      <TableCell>{s.employee_code || ''}</TableCell>
                      <TableCell>{s.employment_type_name || ''}</TableCell>
                      <TableCell>{s.employment_status_name || ''}</TableCell>
                      <TableCell>{s.exit_reason_name || ''}</TableCell>
                      <TableCell>{showDate(s.probation_completed_on)}</TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            s.updatedByName
                              ? `Last updated by ${s.updatedByName} on ${showStamp(s.updatedAt)}`
                              : s.createdAt
                                ? `Created on ${showStamp(s.createdAt)}`
                                : ''
                          }
                        >
                          <Typography variant="body2">
                            {s.createdByName || ''}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Two spells means the person left and came back. Say what the gap is,
                so it does not read as missing data. */}
            {spells.length > 1 && (
              <Alert severity="info" sx={{ m: 2, mt: 1 }}>
                This person has {spells.length} employment records. The gap between
                one exit date and the next joining date is the time they were away.
              </Alert>
            )}
          </Paper>
        )}

        {/* ---------------- the timeline ---------------- */}
        {timeline.length > 0 && (
          <Paper variant="outlined">
            <Box sx={{ p: 2, pb: 1 }}>
              <Typography variant="subtitle2">What changed, and when</Typography>
              <Typography variant="caption" color="text.secondary">
                One line per field. A single save can produce several lines, for
                example moving department and wing together
              </Typography>
            </Box>
            <Divider />
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Change</TableCell>
                    <TableCell>Was</TableCell>
                    <TableCell>Became</TableCell>
                    <TableCell>Changed by</TableCell>
                    <TableCell>Recorded on</TableCell>
                    <TableCell sx={{ minWidth: 280, maxWidth: 480 }}>Note</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeline.map((row, index) => {
                    // group the eye by date: only show the date when it changes
                    const isNewDate =
                      index === 0 || timeline[index - 1].changed_on !== row.changed_on;

                    return (
                      <TableRow key={row.org_change_id}>
                        {/* Hovering says WHICH date this is. It follows the
                            employee's life, not the person recording it: a joining
                            line carries the day they joined, an exit line the day
                            they left, a field change the day it took effect. */}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          {isNewDate ? (
                            <Tooltip
                              title={meaningOfDate(row.org_change_type_id)}
                              placement="top"
                              arrow
                            >
                              <Typography variant="body2" sx={{ cursor: 'help' }}>
                                {showDate(row.changed_on)}
                              </Typography>
                            </Tooltip>
                          ) : (
                            ''
                          )}
                        </TableCell>
                        <TableCell>
                          {/* hovering says WHY this line exists, using the same
                              wording as the Change Types screen */}
                          <Tooltip
                            title={helpForChangeType(row.org_change_type_id, true)}
                            placement="top"
                            arrow
                            componentsProps={HELP_TOOLTIP_PROPS}
                          >
                            <Chip
                              size="small"
                              variant="outlined"
                              label={row.change_name}
                              color={TYPE_COLOUR[row.org_change_type_id] || 'default'}
                              sx={{ cursor: 'help' }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{row.old_value_text || ''}</TableCell>
                        <TableCell>
                          {row.new_value_text ? (
                            <Stack direction="row" spacing={0.5} alignItems="center">
                              <Icon icon="mdi:arrow-right" />
                              <span>{row.new_value_text}</span>
                            </Stack>
                          ) : (
                            ''
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.changedByName || 'system'}
                          </Typography>
                        </TableCell>
                        {/* WHEN IT WAS TYPED IN, which is a different question from
                            the Date column. Date is when the change took effect,
                            this is when somebody sat down and recorded it. They are
                            the same for an ordinary change and far apart for
                            anything backdated: a joining line dated 2023 can have
                            been recorded today, the first time that person was
                            tagged. Showing both is what makes that visible without
                            having to read the note. */}
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">
                            {showStamp(row.recordedAt)}
                          </Typography>
                          {row.recordedAt
                            && String(row.recordedAt).slice(0, 10)
                              !== String(row.changed_on).slice(0, 10) && (
                            <Typography variant="caption" color="text.secondary">
                              recorded later
                            </Typography>
                          )}
                        </TableCell>
                        {/* Notes only appear on rows that need explaining, and when
                            one does appear it is the whole point of the row. It was
                            cut off at a fixed width with a tooltip, which hid the
                            explanation behind a hover on a table that had space to
                            spare. It now takes the rest of the row and wraps. */}
                        <TableCell sx={{ minWidth: 280, maxWidth: 480 }}>
                          {row.note && (
                            <Typography variant="caption" color="text.secondary"
                              sx={{ display: 'block', whiteSpace: 'normal' }}>
                              {row.note}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </Stack>

      {/* Creating history from what we already know. It changes nothing about the
          person, it only records where they sit today, dated from their joining
          date. Marked as seeded, because we cannot know they held this role the
          whole time, only that they hold it now. */}
    </GlobalWrapper>
  );
}
